import { useEffect, useRef, useState } from 'react'
import { Lock, Unlock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCopilotStore, type CopilotTranscriptLine } from '../../stores/copilotStore'

interface Props {
  callSid: string
  readOnly?: boolean
}

// Stable empty reference — returning `?? []` from inside a Zustand selector
// produces a fresh array each call and loops useSyncExternalStore.
const EMPTY_TRANSCRIPT: ReadonlyArray<CopilotTranscriptLine> = []

export function LiveTranscriptPanel({ callSid, readOnly = false }: Props) {
  const { t } = useTranslation('calls')
  const transcript =
    useCopilotStore((s) => s.byCallSid[callSid]?.transcript) ?? EMPTY_TRANSCRIPT
  const [scrollLocked, setScrollLocked] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (readOnly || !scrollLocked || !containerRef.current) return
    containerRef.current.scrollTop = containerRef.current.scrollHeight
  }, [transcript.length, scrollLocked, readOnly])

  return (
    <section className="flex h-full flex-col rounded-lg border border-border bg-surface-card">
      <header className="flex items-center justify-between border-b border-border px-4 py-2">
        <h3 className="text-sm font-semibold text-text-primary">
          {readOnly
            ? t('detail.copilot.transcriptTitle')
            : t('detail.copilot.liveTranscriptTitle')}
        </h3>
        {!readOnly && (
          <button
            type="button"
            onClick={() => setScrollLocked((v) => !v)}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-text-secondary hover:bg-surface-elevated"
            aria-label={scrollLocked ? 'Unlock auto-scroll' : 'Lock auto-scroll'}
          >
            {scrollLocked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
            {scrollLocked ? 'Auto-scroll' : 'Free scroll'}
          </button>
        )}
      </header>
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto px-4 py-3 text-sm"
      >
        {transcript.length === 0 ? (
          <p className="text-center text-text-secondary">Waiting for audio…</p>
        ) : (
          <ul className="space-y-2">
            {transcript.map((line) => (
              <li
                key={line.id}
                className={
                  line.speaker === 'rep'
                    ? 'rounded bg-primary/5 px-3 py-2'
                    : 'rounded bg-surface-elevated px-3 py-2'
                }
              >
                <span
                  className={
                    line.speaker === 'rep'
                      ? 'mr-2 text-xs font-semibold uppercase text-primary'
                      : 'mr-2 text-xs font-semibold uppercase text-text-secondary'
                  }
                >
                  {line.speaker}
                </span>
                <span className="text-text-primary">{line.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
